/**
 * MetricCard (P26).
 *
 * Lifted from `sample-wireframe/client/src/pages/Home.tsx`. Single tile
 * with icon + delta-pill + value + sparkline. The sparkline uses recharts
 * (already a workspace dep) so the visual remains identical.
 */
import type { JSX } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Line, LineChart, ResponsiveContainer } from 'recharts';

interface MetricCardProps {
  icon: LucideIcon;
  label: string;
  value: string;
  delta: string;
  tone?: 'good' | 'warn' | 'danger' | 'neutral';
  spark?: readonly number[];
}

const DEFAULT_SPARK = [42, 55, 49, 61, 58, 72] as const;

export function MetricCard({
  icon: Icon,
  label,
  value,
  delta,
  tone = 'neutral',
  spark = DEFAULT_SPARK,
}: MetricCardProps): JSX.Element {
  const sparkData = spark.map((v, index) => ({ index, value: v }));
  return (
    <article className="metric-card">
      <div className="metric-head">
        <div className="metric-icon">
          <Icon size={18} aria-hidden />
        </div>
        <span className={`metric-delta ${tone}`}>{delta}</span>
      </div>
      <p>{label}</p>
      <h3>{value}</h3>
      <div className="metric-sparkline" aria-hidden>
        <ResponsiveContainer width="100%" height={36}>
          <LineChart data={sparkData}>
            <Line
              type="monotone"
              dataKey="value"
              stroke="currentColor"
              strokeWidth={2}
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </article>
  );
}
